import { setLocale, useLocale, type Locale } from '../lib/i18n'
import { Flag } from './Icon'

/**
 * English / Puerto Rican Spanish.
 *
 * A visible toggle rather than a settings page, because the person who needs it
 * is the one who cannot read the settings page. It shows BOTH flags with the
 * inactive one dimmed, so what it does is obvious before it is pressed — a
 * single flag is ambiguous about whether it shows the current language or the
 * one you would switch to, and people get that wrong about half the time.
 *
 * It used to reload the page, because `setLocale` mutated a module variable and
 * re-rendered nothing. It no longer does: `i18n.subscribe` makes the locale a
 * real external store and the shell subscribes at the root, so the language
 * changes in the same tick. A reload would throw away an unsent note, an open
 * inspection and the queue — on a phone, in a yard, with no signal.
 */
export default function LanguageToggle() {
  const current = useLocale()

  const pick = (next: Locale) => {
    if (next === current) return
    setLocale(next)
  }

  return (
    <div className="lang" role="group" aria-label="Language / Idioma">
      {(['en', 'es-PR'] as const).map((loc) => (
        <button
          key={loc}
          onClick={() => pick(loc)}
          aria-pressed={current === loc}
          aria-label={loc === 'en' ? 'English' : 'Español'}
          title={loc === 'en' ? 'English' : 'Español'}
          className={current === loc ? 'on' : ''}
        >
          <Flag locale={loc} size={22} />
        </button>
      ))}
    </div>
  )
}
